import React, { useRef, useState } from "react";
import Field from "./Formfield";
import { CURRENCY_OPTIONS } from "./Tenantformoptions";
import "./TenantDetailsTab.css";

/* ============================================================
   TENANT TAB
   Props:
   - tenant: tenant state object (from AddTenant.js)
   - errors: flat error map from parent (keys like "tenant.name")
   - onChange: parent's handleTenantChange(e)
   - onFileUpload: parent's handleFileUpload function
   ============================================================ */
const TenantDetailsTab = ({ tenant, errors, onChange, onFileUpload }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError("Please upload a valid image file (PNG, JPEG, JPG, SVG, WEBP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size must be less than 5MB");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      if (onFileUpload) {
        // This will update the logo preview
        await onFileUpload(file);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError("Failed to upload logo. Please try again.");
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = () => {
    const syntheticEvent = {
      target: {
        name: 'logo',
        value: ''
      }
    };
    onChange(syntheticEvent);
    // Clear the stored file
    if (onFileUpload) {
      // You might want to add a function to clear the logo file
    }
  };

  return (
    <div className="at-section">
      <h3 className="at-section__title">Tenant Details</h3>
      <p className="at-section__subtitle">Core identity and access configuration.</p>

      <div className="at-grid">
        <Field label="Name" required error={errors["tenant.name"]}>
          <input name="name" value={tenant.name} onChange={onChange} placeholder="Acme Inspections" />
        </Field>

        <Field label="Legal Name">
          <input name="legal_name" value={tenant.legal_name} onChange={onChange} placeholder="Acme Inspections Pvt Ltd" />
        </Field>

        <Field label="Code" required error={errors["tenant.code"]}>
          <input name="code" value={tenant.code} onChange={onChange} placeholder="ACME" />
        </Field>

        <Field label="Portal Slug">
          <input name="portal_slug" value={tenant.portal_slug} onChange={onChange} placeholder="acme" />
        </Field>

        <Field label="Custom Domain">
          <input name="custom_domain" value={tenant.custom_domain} onChange={onChange} placeholder="portal.acme.com" />
        </Field>

        <Field label="Logo">
          <div className="logo-upload-container">
            {tenant.logo && (
              <div className="logo-preview">
                <img src={tenant.logo} alt="Logo preview" className="logo-preview-image" />
                <button 
                  type="button" 
                  className="logo-remove-btn" 
                  onClick={handleRemoveLogo}
                  aria-label="Remove logo"
                >
                  ×
                </button>
              </div>
            )}
            
            <div className="logo-upload-actions">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                style={{ display: 'none' }}
                id="logo-upload"
              />
              
              <label htmlFor="logo-upload" className="logo-upload-btn">
                <span>📁</span> Upload Logo
              </label>
              
              {isUploading && (
                <div className="upload-spinner">Uploading...</div>
              )}
              
              {uploadError && (
                <div className="upload-error">{uploadError}</div>
              )}
            </div>
          </div>
        </Field>

        <Field label="Default Timezone">
          <input name="default_timezone" value={tenant.default_timezone} onChange={onChange} placeholder="Asia/Kolkata" />
        </Field>

        <Field label="Default Currency">
          <select name="default_currency" value={tenant.default_currency} onChange={onChange}>
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>

        <Field label="Contact Email" error={errors["tenant.contact_email"]}>
          <input type="email" name="contact_email" value={tenant.contact_email} onChange={onChange} placeholder="ops@acme.com" />
        </Field>

        <Field label="Contact Phone">
          <input name="contact_phone" value={tenant.contact_phone} onChange={onChange} placeholder="+1 555 123 4567" />
        </Field>

        <Field label="Status">
          <select name="status" value={tenant.status} onChange={onChange}>
            <option value="ACTIVE">ACTIVE</option>
            <option value="TRIAL">TRIAL</option>
            <option value="INACTIVE">INACTIVE</option>
          </select>
        </Field>
      </div>

      <div className="at-switch-row">
        <label className="at-switch">
          <input
            type="checkbox"
            name="registration_enabled"
            checked={tenant.registration_enabled}
            onChange={onChange}
          />
          <span className="at-switch__track"><span className="at-switch__thumb" /></span>
          <span className="at-switch__label">Registration Enabled</span>
        </label>

        <label className="at-switch">
          <input
            type="checkbox"
            name="login_enabled"
            checked={tenant.login_enabled}
            onChange={onChange}
          />
          <span className="at-switch__track"><span className="at-switch__thumb" /></span>
          <span className="at-switch__label">Login Enabled</span>
        </label>
      </div>
    </div>
  );
};

export default TenantDetailsTab;